/**
 * Horizontal Routing
 * 
 * Side-to-side connections: target is to the left or right of source
 * Connection: right/left of source → left/right of target
 */

import { NODE_WIDTH, NODE_HEIGHT, CORNER_RADIUS, MIN_PERPENDICULAR_DISTANCE, ROUTING_OBSTACLE_INFLATION } from '../constants';
import { buildPathWithCorners } from '../pathBuilders';
import { filterRedundantPoints } from './repairPath';

const EXIT_CLEARANCE = MIN_PERPENDICULAR_DISTANCE + CORNER_RADIUS;

function collectBlockingObstacles(obstacles, minY, maxY) {
  const blocking = [];
  for (const node of obstacles) {
    if (!node.pos || !node.dims) continue;
    const nodeLeft = node.pos.x;
    const nodeRight = node.pos.x + (node.dims.width || NODE_WIDTH);
    const nodeTop = node.pos.y;
    const nodeBottom = node.pos.y + (node.dims.height || NODE_HEIGHT);
    
    if (nodeBottom > minY - ROUTING_OBSTACLE_INFLATION && nodeTop < maxY + ROUTING_OBSTACLE_INFLATION) {
      blocking.push({ nodeLeft, nodeRight, nodeTop, nodeBottom, id: node.id });
    }
  }
  return blocking;
}

function checkVerticalDetour(startX, startY, endX, blockingObstacles) {
  for (const obs of blockingObstacles) {
    const startYInObstacle = startY > obs.nodeTop - ROUTING_OBSTACLE_INFLATION && startY < obs.nodeBottom + ROUTING_OBSTACLE_INFLATION;
    const startXRightOfObs = startX > obs.nodeRight;
    const endXLeftOfObs = endX < obs.nodeLeft;
    
    if (startYInObstacle && startXRightOfObs && endXLeftOfObs) {
      return { needed: true, detourY: obs.nodeTop - ROUTING_OBSTACLE_INFLATION * 2 };
    }
    
    const endXRightOfObs = endX > obs.nodeRight;
    const startXLeftOfObs = startX < obs.nodeLeft;
    
    if (startYInObstacle && startXLeftOfObs && endXRightOfObs) {
      return { needed: true, detourY: obs.nodeTop - ROUTING_OBSTACLE_INFLATION * 2 };
    }
  }
  return { needed: false, detourY: null };
}

function findClearMidX(midX, blockingObstacles, goingRight) {
  const isVerticalBlocked = (x) => {
    for (const obs of blockingObstacles) {
      if (x > obs.nodeLeft - ROUTING_OBSTACLE_INFLATION && x < obs.nodeRight + ROUTING_OBSTACLE_INFLATION) {
        return obs;
      }
    }
    return null;
  };
  
  let blocker = isVerticalBlocked(midX);
  let attempts = 0;
  
  while (blocker && attempts < 20) {
    if (goingRight) {
      midX = blocker.nodeRight + ROUTING_OBSTACLE_INFLATION * 2;
    } else {
      midX = blocker.nodeLeft - ROUTING_OBSTACLE_INFLATION * 2;
    }
    blocker = isVerticalBlocked(midX);
    attempts++;
  }
  
  return midX;
}

/**
 * Route horizontally (target is to the left or right of source)
 */
export function routeHorizontal(startX, startY, endX, endY, fromPos, toPos, fromDims, toDims, obstacles, goingRight = true, edgeId = null, laneAssignment = null) {
  let midX = (startX + endX) / 2;
  
  const minY = Math.min(startY, endY);
  const maxY = Math.max(startY, endY);
  
  const blockingObstacles = collectBlockingObstacles(obstacles, minY, maxY);
  
  const detour = checkVerticalDetour(startX, startY, endX, blockingObstacles);
  if (detour.needed) {
    const exitX = goingRight ? startX + EXIT_CLEARANCE : startX - EXIT_CLEARANCE;
    const entryX = goingRight ? endX - EXIT_CLEARANCE : endX + EXIT_CLEARANCE;
    
    const points = [
      { x: startX, y: startY },
      { x: exitX, y: startY },
      { x: exitX, y: detour.detourY },
      { x: entryX, y: detour.detourY },
      { x: entryX, y: endY },
      { x: endX, y: endY },
    ];
    
    return buildPathWithCorners(filterRedundantPoints(points), CORNER_RADIUS);
  }
  
  if (blockingObstacles.length > 0) {
    midX = findClearMidX(midX, blockingObstacles, goingRight);
  }
  
  const points = [
    { x: startX, y: startY },
    { x: midX, y: startY },
    { x: midX, y: endY },
    { x: endX, y: endY },
  ];
  
  return buildPathWithCorners(filterRedundantPoints(points), CORNER_RADIUS);
}
